import { makeStyles } from '@mui/styles';
import Peek from 'apollo-react/components/Peek';
import TreeItem from 'apollo-react/components/TreeItem';
import TreeView from 'apollo-react/components/TreeView';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getMEDDRAChildren, getMEDDRADictionary } from '../../actions/RegulatoryTermsActions';
import hlgtIcon from '../../assets/images/regulatory-terms/HLGT.gif';
import hltIcon from '../../assets/images/regulatory-terms/HLT.gif';
import lltIcon from '../../assets/images/regulatory-terms/LLT.gif';
import lltNonIcon from '../../assets/images/regulatory-terms/llt_non.gif';
import ptIcon from '../../assets/images/regulatory-terms/PT.gif';
import socIcon from '../../assets/images/regulatory-terms/SOC.gif';
import Loader from '../../components/CustomLoader';
import { MEDDRA_DICTIONARY_ROWS_PER_PAGE } from '../../constants/Common';
import { useTranslation } from '../../custom-hooks/useTranslation';

const useStyles = makeStyles({
  loadingContainer: {
    position: 'absolute',
    top: 0,
    width: '95%',
    height: '100%',
    background: 'rgb(255,255,255,0.8)',
  },
  itemLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    '& > img': {
      marginRight: 6,
    },
  },
});

const MEDDRATree = ({
  termHierarchy = '',
  onSelectItem,
  searchText,
  isReverse,
  paginationPage,
  setPaginationCount,
  handleSelectRepository,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { MEDDRADictionary: dictionary, isNewDict, loading, hasError } = useSelector(
    ({ regulatoryDictionaries }) => regulatoryDictionaries
  );
  const [selectedNode, setSelectedNode] = useState();
  const [nodePath, setNodePath] = useState();

  const [expandedNodes, setExpandedNodes] = useState([]);
  const translateString = useTranslation();

  const mapNodes = (parentNode) => (acc, item) => {
    if (_.isNil(item)) {
      return;
    }

    item.parentNode = parentNode;
    const itemChildren = item.Children ? item.Children.reduce(mapNodes(item), {}) : [];

    const { TermHierarchy, Code, ParentCode } = item;
    const currentParentCode = TermHierarchy === 'SOC' ? '' : `-${ParentCode}`;
    item.nodeId = `${TermHierarchy}-${Code}${currentParentCode}`;

    return {
      ...acc,
      [item.nodeId]: item,
      ...itemChildren,
    };
  };

  const items = useMemo(() => {
    return dictionary.reduce(mapNodes(), {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictionary]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isNewDict && setExpandedNodes([]);
  }, [isNewDict]);

  useEffect(() => {
    if (!_.isEmpty(items) && !_.isNil(selectedNode)) {
      const currentNode = _.isEmpty(selectedNode.Children)
        ? items[selectedNode.nodeId]
        : selectedNode;
      const currentPath = getNodePath(currentNode);
      setNodePath(currentPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedNode]);

  useEffect(() => {
    if (!_.isNil(selectedNode)) {
      const { Name: name, Code: code, isDoubleClicked } = selectedNode;
      const selectable = isSelectable(selectedNode);
      const currentSelectedNode = { name, code, selectable };

      if (selectable && isDoubleClicked) {
        handleSelectRepository();
        return;
      }

      onSelectItem(currentSelectedNode, nodePath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodePath]);

  useEffect(() => {
    setPaginationCount(dictionary.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictionary.length]);

  const fetchData = (childrenData = null, path) => {
    const defaultData = {
      HierarchyId: '',
      TextSearchOption: 0,
      TermHierarchy: termHierarchy,
      Dictionary: 'MEDDRA',
      SearchText: searchText,
      SearchBy: _.isEmpty(searchText) ? '' : 'LLT',
      ShowAll: _.isEmpty(searchText),
    };

    _.isNil(path)
      ? dispatch(getMEDDRADictionary(defaultData))
      : dispatch(getMEDDRAChildren(childrenData, path));
  };

  const getNodePath = (node) => {
    if (_.isNil(node)) {
      return [];
    }

    const path = [];
    const currentNode = node;

    let parent = null;
    if (isReverse) {
      parent = currentNode;
      while (!_.isNil(parent)) {
        path.push(parent);
        parent = parent.Children[0];
      }
    } else {
      path.push(currentNode);
      parent = currentNode.parentNode;

      while (!_.isNil(parent)) {
        path.push(parent);
        parent = parent.parentNode;
      }
    }

    return path.reverse();
  };

  const isSelectable = (node) => _.includes(['PT', 'LLT'], node.TermHierarchy);

  const handleSelect = (event, nodeId) => {
    const { Id: HierarchyId, Children, parentNode, ...node } = items[nodeId];
    const selectedNodePath = getNodePath({ ...node, Children, parentNode });
    const isDoubleClicked = event.detail === 2;

    if (!isLeaf(node) && _.isEmpty(Children)) {
      fetchData(
        {
          ...node,
          HierarchyId,
          isTopDown: !isReverse,
        },
        selectedNodePath
      );
    }

    setSelectedNode({ ...node, Children, parentNode, isDoubleClicked });
  };

  const getIcon = (node) => {
    switch (node.TermHierarchy) {
      case 'SOC':
        return socIcon;
      case 'HLGT':
        return hlgtIcon;
      case 'HLT':
        return hltIcon;
      case 'PT':
        return ptIcon;
      case 'LLT':
        return node.UDF9 === 'N' ? lltNonIcon : lltIcon;
      default:
        return '';
    }
  };

  const getLabel = (node) => {
    const { Code, Name } = node;
    return (
      <div className={classes.itemLabelContainer}>
        <img src={getIcon(node)} alt="code icon" />
        {`${Name} (${Code})`}
      </div>
    );
  };

  const handleExpandedNodes = (_, nodeIds) => setExpandedNodes(nodeIds);

  const isLeaf = ({ TermHierarchy }) => {
    return isReverse ? TermHierarchy === 'SOC' : TermHierarchy === 'LLT';
  };

  const renderTreeItems = (node) => {
    const { nodeId, Children } = node;

    return (
      <TreeItem
        key={nodeId}
        nodeId={nodeId}
        label={
          node.UDF4 ? (
            <Peek
              placement="bottom"
              anchor={<div>{getLabel(node)}</div>}
              content={<div style={{ maxWidth: 400 }}>{node.UDF4}</div>}
            />
          ) : (
            getLabel(node)
          )
        }
      >
        {!isLeaf(node) && _.isArray(Children) && _.isEmpty(Children) ? (
          <TreeItem
            key={`${nodeId}-empty`}
            nodeId={`${nodeId}-empty`}
            label={translateString('No data to display')}
          />
        ) : (
          Children.map((child) => renderTreeItems(child))
        )}
      </TreeItem>
    );
  };

  return loading ? (
    <div className={classes.loadingContainer}>
      <Loader />
    </div>
  ) : hasError ? (
    <div>{translateString('No data to display')}</div>
  ) : (
    <TreeView
      onNodeSelect={handleSelect}
      onNodeToggle={handleExpandedNodes}
      hideArrows={false}
      expanded={expandedNodes}
    >
      {dictionary
        .slice(
          paginationPage * MEDDRA_DICTIONARY_ROWS_PER_PAGE,
          (paginationPage + 1) * MEDDRA_DICTIONARY_ROWS_PER_PAGE
        )
        .map(renderTreeItems)}
    </TreeView>
  );
};

export default MEDDRATree;
